import { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'lib/dayjs';
import { toast } from 'react-toastify';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import {
  useGetLastTimeLogByDateQuery,
  useOfficialCheckoutMutation,
  useCheckOfficialCheckoutOverlapsMutation,
} from 'redux/features/timeLogApi';

const OfficialLeaveModal = ({ isOpen, onClose }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [leaveType, setLeaveType] = useState('OFFICIAL_CHECKOUT'); // OFFICIAL_CHECKOUT, OFFICIAL_LEAVE_FULL, OFFICIAL_LEAVE_HALF
  const [halfDayType, setHalfDayType] = useState('FIRST_HALF'); // FIRST_HALF, SECOND_HALF
  const [selectedTime, setSelectedTime] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set()); // Can be user_id or log_id depending on leaveType
  const [expandedSections, setExpandedSections] = useState({
    Standard: true,
    'Time Overlap Employees': true,
    'Not Checked In': true,
    'On Leave': true,
  });
  const [skippedUsers, setSkippedUsers] = useState([]);

  const { data: userData, isLoading: isLoadingUserData } = useGetLastTimeLogByDateQuery(
    { date: selectedDate },
    { skip: !selectedDate },
  );

  const [processOfficialLeave, { isLoading: isProcessing }] = useOfficialCheckoutMutation();
  const [checkOverlaps] = useCheckOfficialCheckoutOverlapsMutation();

  /* ------------------------ HELPERS ------------------------ */

  const isWorkedMore = useCallback((userCheckoutTime, selectedTimeStr) => {
    if (!selectedTimeStr || !userCheckoutTime) return false;
    const checkout = dayjs(userCheckoutTime);
    const [hours, minutes] = selectedTimeStr.split(':');
    const selected = dayjs(userCheckoutTime).hour(hours).minute(minutes).second(0).millisecond(0);
    return checkout.isAfter(selected);
  }, []);

  /* ------------------------ DERIVED STATE ------------------------ */

  const filteredUsers = useMemo(() => {
    if (!userData) return [];
    return userData.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const empId = user.employee_id?.toString().toLowerCase() || '';
      return fullName.includes(searchLower) || empId.includes(searchLower);
    });
  }, [userData, searchQuery]);

  const categorizedUsers = useMemo(() => {
    const groups = {
      Standard: [],
      'Time Overlap Employees': [],
      'Not Checked In': [],
      'On Leave': [],
    };
    filteredUsers.forEach((user) => {
      // Check if user is in overlapping list
      const isOverlap = skippedUsers.some((su) => su.userId === user.user_id);

      if (isOverlap) {
        groups['Time Overlap Employees'].push(user);
      } else if (groups[user.status]) {
        groups[user.status].push(user);
      }
    });
    return groups;
  }, [filteredUsers, skippedUsers]);

  /* ------------------------ EFFECTS ------------------------ */

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(dayjs().format('YYYY-MM-DD'));
      setLeaveType('OFFICIAL_CHECKOUT');
      setHalfDayType('FIRST_HALF');
      setSelectedTime('');
      setSearchQuery('');
      setSelectedIds(new Set());
      setSkippedUsers([]);
    }
  }, [isOpen]);

  // Handle overlap check — runs against ALL loaded users so the "Time Overlap Employees" section
  // is always accurate, even before the admin has ticked individual checkboxes.
  useEffect(() => {
    const handleCheckOverlaps = async () => {
      if (
        !selectedDate ||
        leaveType === 'OFFICIAL_CHECKOUT' ||
        leaveType === 'OFFICIAL_LEAVE_FULL' ||
        !userData ||
        userData.length === 0
      ) {
        setSkippedUsers([]);
        return;
      }

      // Always check all users on the date so switching FIRST_HALF ↔ SECOND_HALF
      // immediately updates which users are shown as overlapping.
      const allUserIds = userData.map((u) => u.user_id);

      const payload = {
        action: leaveType,
        date: selectedDate,
        userIds: allUserIds,
        halfDayType: leaveType === 'OFFICIAL_LEAVE_HALF' ? halfDayType : undefined,
      };

      try {
        const result = await checkOverlaps(payload).unwrap();
        setSkippedUsers(result || []);
      } catch (error) {
        console.error('Overlap check failed:', error);
      }
    };

    const timeoutId = setTimeout(handleCheckOverlaps, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [selectedDate, userData, leaveType, halfDayType, checkOverlaps]);

  // Handle auto-selection logic
  useEffect(() => {
    if (!userData) return;

    const newSelected = new Set();
    if (leaveType === 'OFFICIAL_CHECKOUT') {
      // For checkout, select Standard users who haven't worked more than selected time
      categorizedUsers.Standard.forEach((user) => {
        if (!selectedTime || !isWorkedMore(user.last_check_out, selectedTime)) {
          newSelected.add(user.id);
        }
      });
    } else {
      // For leave, by default don't auto-select? Actually, user might want to select all "Not Checked In"
      // Let's leave it empty and let user use "Select All"
    }
    setSelectedIds(newSelected);
  }, [userData, leaveType, selectedTime, categorizedUsers.Standard, isWorkedMore]);

  /* ------------------------ HANDLERS ------------------------ */

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleSelectAllSection = (sectionUsers) => {
    const sectionIds = sectionUsers.map((u) =>
      leaveType === 'OFFICIAL_CHECKOUT' ? u.id : u.user_id,
    );
    const allSelected = sectionIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        sectionIds.forEach((id) => next.delete(id));
      } else {
        sectionIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedDate || selectedIds.size === 0) return;
    if (leaveType === 'OFFICIAL_CHECKOUT' && !selectedTime) {
      toast.error('Please select checkout time');
      return;
    }

    const payload = {
      action: leaveType,
      date: selectedDate,
      time: leaveType === 'OFFICIAL_CHECKOUT' ? selectedTime : undefined,
      halfDayType: leaveType === 'OFFICIAL_LEAVE_HALF' ? halfDayType : undefined,
    };

    if (leaveType === 'OFFICIAL_CHECKOUT') {
      payload.logIds = Array.from(selectedIds);
    } else {
      payload.userIds = Array.from(selectedIds);
    }

    try {
      const result = await processOfficialLeave(payload).unwrap();
      toast.success(result.message);
      onClose();
    } catch (error) {
      console.error('Action failed:', error);
      toast.error(error?.data?.message || 'Action failed');
    }
  };

  /* ------------------------ RENDER HELPERS ------------------------ */

  const renderUserRow = (user) => {
    const id = leaveType === 'OFFICIAL_CHECKOUT' ? user.id : user.user_id;
    const checked = selectedIds.has(id);
    const workedMore =
      leaveType === 'OFFICIAL_CHECKOUT' &&
      selectedTime &&
      isWorkedMore(user.last_check_out, selectedTime);
    const showCheckbox = !(leaveType === 'OFFICIAL_CHECKOUT' && !user.id);

    const content = (
      <>
        <div className="flex items-center gap-3">
          {showCheckbox && (
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggleUser(id)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          )}
          <div>
            <div className={`font-medium ${workedMore ? 'text-amber-700' : 'text-gray-800'}`}>
              {user.first_name} {user.last_name}
              {user.leave_type && (
                <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase font-bold">
                  {user.leave_type.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1.5">
              <span>Emp ID: {user.employee_id}</span>
              {user.last_check_in && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>In: {dayjs(user.last_check_in).format('hh:mm A')}</span>
                </>
              )}
              {user.last_check_out && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>Out: {dayjs(user.last_check_out).format('hh:mm A')}</span>
                </>
              )}
            </div>
          </div>
        </div>
        {workedMore && (
          <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
            Worked More
          </span>
        )}
      </>
    );

    const commonClasses = `flex items-center justify-between border-t px-4 py-2.5 text-sm transition-colors ${
      checked ? 'bg-blue-50/30' : 'hover:bg-gray-50'
    }`;

    if (showCheckbox) {
      return (
        <label
          key={user.user_id + '_' + (user.id || 'none')}
          className={`${commonClasses} cursor-pointer`}
        >
          {content}
        </label>
      );
    }

    return (
      <div
        key={user.user_id + '_' + (user.id || 'none')}
        className={`${commonClasses} cursor-default`}
      >
        {content}
      </div>
    );
  };

  const renderSection = (title, users, colorClass) => {
    if (users.length === 0) return null;
    const isExpanded = expandedSections[title];
    const sectionIds = users
      .filter((u) => leaveType !== 'OFFICIAL_CHECKOUT' || u.id)
      .map((u) => (leaveType === 'OFFICIAL_CHECKOUT' ? u.id : u.user_id));
    const allSelected = sectionIds.length > 0 && sectionIds.every((id) => selectedIds.has(id));

    return (
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-4">
        <div
          className={`flex items-center justify-between px-4 py-2 border-b border-gray-100 cursor-pointer ${colorClass}`}
          onClick={() => toggleSection(title)}
        >
          <div className="flex items-center gap-2">
            {sectionIds.length > 0 && (
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleSelectAllSection(
                    users.filter((u) => leaveType !== 'OFFICIAL_CHECKOUT' || u.id),
                  );
                }}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            )}
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{title}</span>
            <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
              {users.length}
            </span>
          </div>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {isExpanded && <div className="divide-y divide-gray-100">{users.map(renderUserRow)}</div>}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Official Leave / Checkout</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage bulk official checkout or leave logs
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col overflow-hidden">
          {/* Filters Section */}
          <div className="px-6 py-4 space-y-4 border-b border-gray-100 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  max={dayjs().format('YYYY-MM-DD')}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </label>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="OFFICIAL_CHECKOUT">Official Checkout</option>
                  <option value="OFFICIAL_LEAVE_FULL">Full Day Official Leave</option>
                  <option value="OFFICIAL_LEAVE_HALF">Half Day Official Leave</option>
                </select>
              </div>

              {leaveType === 'OFFICIAL_CHECKOUT' && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Checkout Time
                  </label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              {leaveType === 'OFFICIAL_LEAVE_HALF' && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Half Day Type
                  </label>
                  <select
                    value={halfDayType}
                    onChange={(e) => setHalfDayType(e.target.value)}
                    className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="FIRST_HALF">First Half</option>
                    <option value="SECOND_HALF">Second Half</option>
                  </select>
                </div>
              )}
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search by name or employee ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-9 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
                disabled={!selectedDate}
              />
            </div>
          </div>

          {/* List Area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/30 p-4">
            {isLoadingUserData && (
              <div className="flex justify-center py-8">
                <ThreeDotLoading />
              </div>
            )}

            {!isLoadingUserData && !selectedDate && (
              <div className="text-center py-10 text-gray-400 text-sm">
                Select a date to view users
              </div>
            )}

            {!isLoadingUserData && selectedDate && filteredUsers.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">No users found</div>
            )}

            {!isLoadingUserData && (
              <>
                {renderSection('Standard', categorizedUsers.Standard, 'bg-gray-100/80')}
                {renderSection(
                  'Time Overlap Employees',
                  categorizedUsers['Time Overlap Employees'],
                  'bg-amber-50/80',
                )}
                {renderSection(
                  'Not Checked In',
                  categorizedUsers['Not Checked In'],
                  'bg-orange-50/80',
                )}
                {renderSection('On Leave', categorizedUsers['On Leave'], 'bg-blue-50/80')}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t px-6 py-4 bg-white z-10">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedDate || selectedIds.size === 0 || isProcessing}
            className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50 min-w-[140px]"
          >
            {isProcessing ? (
              <ThreeDotLoading size="sm" color="white" />
            ) : (
              `Save (${selectedIds.size})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfficialLeaveModal;
